import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, LinearProgress, Paper, Typography } from '@mui/material';
import {
    Archive,
    Audiotrack,
    CheckCircle,
    Close,
    Code,
    Delete,
    Description,
    Error,
    Image,
    InsertDriveFileOutlined,
    Movie,
    PauseCircle,
    Upload,
} from '@mui/icons-material';
import { useUploads, useUploadActions, formatBytes, formatETA } from '../uploads/UploadsContext';

const STATUS_TEXT = {
    done: "Completed",
    cancelled: "Cancelled",
    error: "Failed",
};

const STATUS_ICON = {
    done: <CheckCircle color="success" fontSize="small" />,
    cancelled: <PauseCircle color="disabled" fontSize="small" />,
    error: <Error color="error" fontSize="small" />,
};

const IMAGE_EXT = ["png", "jpg", "jpeg", "webp", "gif", "svg", "avif", "heic", "heif", "bmp", "tif", "tiff", "jfif", "apng", "ico"];
const VIDEO_EXT = ["mp4", "webm", "mov", "avi", "mkv", "flv", "wmv", "m4v", "ogv", "mpg", "mpeg", "ts", "3gp"];
const AUDIO_EXT = ["mp3", "wav", "ogg", "oga", "m4a", "aac", "flac", "opus", "weba", "wma", "midi", "mid"];
const ARCHIVE_EXT = ["zip", "rar", "7z", "tar", "gz", "bz2", "xz", "tgz", "iso", "cab", "arj"];
const CODE_EXT = ["js", "jsx", "ts", "tsx", "py", "java", "c", "cpp", "cxx", "h", "hpp", "cs", "go", "rs", "rb", "php", "swift", "kt", "sh", "html", "htm", "css", "scss", "json", "xml", "yml", "yaml", "sql"];

const FILE_ICONS = {
    image: <Image color="primary" fontSize="small" />,
    video: <Movie color="primary" fontSize="small" />,
    audio: <Audiotrack color="primary" fontSize="small" />,
    archive: <Archive color="primary" fontSize="small" />,
    code: <Code color="primary" fontSize="small" />,
    document: <Description color="primary" fontSize="small" />,
};

function getFileType(filename = "") {
    const ext = (filename.split(".").pop() || "").toLowerCase();
    if (IMAGE_EXT.includes(ext)) return "image";
    if (VIDEO_EXT.includes(ext)) return "video";
    if (AUDIO_EXT.includes(ext)) return "audio";
    if (ARCHIVE_EXT.includes(ext)) return "archive";
    if (CODE_EXT.includes(ext)) return "code";
    if (ext) return "document";
    return null;
}

const UploadsFloating = () => {
    const uploads = useUploads();
    const { cancelUpload, cancelAll, clearCompleted, removeUpload } = useUploadActions();
    const [open, setOpen] = useState(false);
    const [, forceUpdate] = useReducer((x) => x + 1, 0);

    const uploadsRef = useRef(uploads);
    uploadsRef.current = uploads;
    const bytesRef = useRef({});
    const smoothedSpeedRef = useRef({});
    const totalSpeedRef = useRef(0);
    const lastSampleRef = useRef(0);

    const activeUploads = uploads.filter((u) => u.status === "uploading");

    useEffect(() => {
        if (activeUploads.length === 0) {
            bytesRef.current = {};
            smoothedSpeedRef.current = {};
            totalSpeedRef.current = 0;
            lastSampleRef.current = 0;
            return;
        }

        const tick = () => {
            const now = Date.now();
            const dt = (now - (lastSampleRef.current || now)) / 1000;
            const prevBytes = bytesRef.current;
            const prevSmooth = smoothedSpeedRef.current;
            const nextBytes = {};
            const nextSmooth = {};
            let totalSpeed = 0;

            const active = uploadsRef.current.filter((u) => u.status === "uploading");
            for (const u of active) {
                const inst = prevBytes[u.id] != null && dt > 0
                    ? Math.max(0, (u.loaded - prevBytes[u.id]) / dt)
                    : 0;
                const prev = prevSmooth[u.id];
                nextSmooth[u.id] = prev != null && prev > 0 ? prev * 0.7 + inst * 0.3 : inst;
                nextBytes[u.id] = u.loaded;
                totalSpeed += nextSmooth[u.id];
            }

            bytesRef.current = nextBytes;
            smoothedSpeedRef.current = nextSmooth;
            totalSpeedRef.current = totalSpeed;
            lastSampleRef.current = now;
            forceUpdate();
        };

        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [activeUploads.length]);

    if (!uploads.length) return null;

    const totalBytes = uploads.reduce((s, u) => s + (u.size || 0), 0);
    const loadedBytes = uploads.reduce((s, u) => s + (u.loaded || 0), 0);
    const remainingBytes = Math.max(0, totalBytes - loadedBytes);
    const pct = totalBytes > 0 ? Math.min(100, (loadedBytes / totalBytes) * 100) : 0;
    const totalSpeed = totalSpeedRef.current || 0;
    const eta = totalSpeed > 0 ? remainingBytes / totalSpeed : null;

    return (
        <>
            <Paper
                elevation={6}
                onClick={() => setOpen((o) => !o)}
                sx={{
                    position: "fixed",
                    bottom: 16,
                    right: 16,
                    zIndex: 1300,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    pr: 2,
                    borderRadius: 3,
                    backgroundColor: "#202020",
                    gap: 1.5,
                    maxWidth: "min(90vw, 460px)",
                }}
            >
                <Box sx={{ position: "relative", display: "flex", flexShrink: 0 }}>
                    <CircularProgress variant="determinate" value={pct} size={44} thickness={5} />
                    <Typography
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 700,
                        }}
                    >
                        {Math.round(pct)}%
                    </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap variant="body2" fontWeight={600}>
                        {activeUploads.length > 0
                            ? `${activeUploads.length} file${activeUploads.length > 1 ? "s" : ""} remaining`
                            : "Uploads complete"}
                    </Typography>
                    <Typography noWrap variant="caption" color="text.secondary">
                        {activeUploads.length > 0 && eta != null ? `${formatETA(eta)} left · ` : ""}
                        {activeUploads.length > 0 ? `${formatBytes(totalSpeed)}/s · ` : ""}
                        {formatBytes(loadedBytes)} / {formatBytes(totalBytes)}
                    </Typography>
                </Box>
            </Paper>

            {open && (
                <Paper
                    elevation={8}
                    sx={{
                        position: "fixed",
                        bottom: 76,
                        right: 16,
                        zIndex: 1300,
                        width: "min(90vw, 380px)",
                        maxHeight: "60vh",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#212121",
                        borderRadius: 3,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1.25, backgroundColor: "#2a2a2a" }}>
                        <Upload color="primary" />
                        <Typography sx={{ ml: 1, flex: 1, fontWeight: 700 }}>Uploads</Typography>
                        {activeUploads.length > 0 && (
                            <Button size="small" color="error" onClick={cancelAll}>
                                Cancel All
                            </Button>
                        )}
                        <IconButton size="small" onClick={() => setOpen(false)}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1 }}>
                        {uploads.map((u) => {
                            const perPct = u.size > 0 ? Math.min(100, (u.loaded / u.size) * 100) : 0;
                            const active = u.status === "uploading";
                            const speed = smoothedSpeedRef.current[u.id] || 0;
                            const type = getFileType(u.filename);

                            return (
                                <Box key={u.id} sx={{ py: 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box sx={{ display: "flex", flexShrink: 0, color: "text.secondary" }}>
                                            {FILE_ICONS[type] || <InsertDriveFileOutlined fontSize="small" />}
                                        </Box>
                                        <Typography noWrap sx={{ flex: 1, fontSize: 13 }}>
                                            {u.filename}
                                        </Typography>
                                        {active ? (
                                            <IconButton size="small" onClick={() => cancelUpload(u.id)} title="Cancel">
                                                <Close fontSize="small" color="error" />
                                            </IconButton>
                                        ) : (
                                            <IconButton size="small" onClick={() => removeUpload(u.id)} title="Remove">
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={perPct}
                                        sx={{ height: 4, borderRadius: 2, my: 0.5 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        {STATUS_ICON[u.status]}
                                        {active
                                            ? `${formatBytes(u.loaded)} / ${formatBytes(u.size)}${speed > 0 ? ` · ${formatBytes(speed)}/s` : ""}`
                                            : STATUS_TEXT[u.status]}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ px: 2, py: 1, borderTop: "1px solid #333333" }}>
                        <Button
                            size="small"
                            onClick={clearCompleted}
                            disabled={!uploads.some((u) => u.status !== "uploading")}
                        >
                            Clear Completed
                        </Button>
                    </Box>
                </Paper>
            )}
        </>
    );
};

export default UploadsFloating;
